import { useState } from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { exhibitionService, type ExhibitionResponse } from '../../services';

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
`;

const DialogContent = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ErrorIcon = styled.div`
  font-size: 28px;
  color: ${({ theme }) => theme.colors.error};
  flex-shrink: 0;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const DialogTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
`;

const ErrorMessage = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.error};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const DialogBody = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SectionTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
`;

const FeaturedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
`;

const FeaturedItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme, $isSelected }) => 
    $isSelected ? theme.colors.primary : theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $isSelected }) => 
    $isSelected ? `${theme.colors.primary}10` : theme.colors.background};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => `${theme.colors.primary}05`};
  }
`;

const FeaturedBadge = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  flex-shrink: 0;
`;

const ExhibitionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ExhibitionTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExhibitionMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const Checkbox = styled.input`
  margin: 0;
  transform: scale(1.2);
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const DialogActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.gray.light};
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${({ $variant = 'secondary', theme }) => {
    switch ($variant) {
      case 'primary':
        return `
          background-color: ${theme.colors.primary};
          color: ${theme.colors.white};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.secondary};
          }
        `;
      case 'danger':
        return `
          background-color: ${theme.colors.error};
          color: ${theme.colors.white};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.error}dd;
          }
        `;
      default:
        return `
          background-color: transparent;
          color: ${theme.colors.gray.dark};
          border: 1px solid ${theme.colors.gray.medium};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.gray.light};
          }
        `;
    }
  }}
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.gray.dark};
  font-style: italic;
  padding: ${({ theme }) => theme.spacing.lg};
`;

interface FeaturedExhibitionLimitDialogProps {
  isOpen: boolean;
  onClose: () => void;
  featuredExhibitions: ExhibitionResponse[];
  onSuccess?: () => void;
}

export const FeaturedExhibitionLimitDialog = ({ 
  isOpen, 
  onClose, 
  featuredExhibitions,
  onSuccess 
}: FeaturedExhibitionLimitDialogProps) => {
  const queryClient = useQueryClient();
  const [selectedExhibitions, setSelectedExhibitions] = useState<Set<number>>(new Set());

  const unfeatureMutation = useMutation({
    mutationFn: async (exhibitionIds: number[]) => {
      // Unfeature selected exhibitions in parallel
      await Promise.all(
        exhibitionIds.map(id => exhibitionService.unfeatureExhibition(id))
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'exhibitions'] });
      setSelectedExhibitions(new Set());
      onSuccess?.();
      onClose();
    },
  });

  const handleExhibitionToggle = (exhibitionId: number) => {
    setSelectedExhibitions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(exhibitionId)) {
        newSet.delete(exhibitionId);
      } else {
        newSet.add(exhibitionId);
      }
      return newSet;
    });
  };

  const handleUnfeatureSelected = () => {
    if (selectedExhibitions.size > 0) {
      unfeatureMutation.mutate(Array.from(selectedExhibitions));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen}>
      <DialogContent>
        <DialogHeader>
          <ErrorIcon>⚠️</ErrorIcon>
          <HeaderContent>
            <DialogTitle>Featured Exhibition Limit Reached</DialogTitle>
            <ErrorMessage>
              Cannot feature more than 5 exhibitions at once
            </ErrorMessage>
          </HeaderContent>
        </DialogHeader>

        <DialogBody>
          <SectionTitle>
            Currently Featured Exhibitions ({featuredExhibitions.length}/5)
          </SectionTitle>
          <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '15px' }}>
            Select exhibitions to unfeature, then try featuring your desired exhibition again.
          </p>

          {featuredExhibitions.length > 0 ? (
            <FeaturedList>
              {featuredExhibitions.map((exhibition) => (
                <FeaturedItem
                  key={exhibition.exhibition_id}
                  $isSelected={selectedExhibitions.has(exhibition.exhibition_id)}
                  onClick={() => handleExhibitionToggle(exhibition.exhibition_id)}
                >
                  <Checkbox
                    type="checkbox"
                    checked={selectedExhibitions.has(exhibition.exhibition_id)}
                    onChange={() => handleExhibitionToggle(exhibition.exhibition_id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <FeaturedBadge>✨ Featured</FeaturedBadge>
                  <ExhibitionInfo>
                    <ExhibitionTitle>{exhibition.title}</ExhibitionTitle>
                    <ExhibitionMeta>
                      {exhibition.artwork_count || 0} artworks • Created {formatDate(exhibition.created_at)}
                    </ExhibitionMeta>
                  </ExhibitionInfo>
                </FeaturedItem>
              ))}
            </FeaturedList>
          ) : (
            <LoadingMessage>Loading featured exhibitions...</LoadingMessage>
          )}
        </DialogBody>

        <DialogActions>
          <ActionButton onClick={onClose} disabled={unfeatureMutation.isPending}>
            Cancel
          </ActionButton>
          <ActionButton
            $variant="danger"
            onClick={handleUnfeatureSelected}
            disabled={selectedExhibitions.size === 0 || unfeatureMutation.isPending}
          >
            {unfeatureMutation.isPending 
              ? `Unfeaturing ${selectedExhibitions.size} exhibition${selectedExhibitions.size !== 1 ? 's' : ''}...`
              : `Unfeature Selected (${selectedExhibitions.size})`
            }
          </ActionButton>
        </DialogActions>
      </DialogContent>
    </Overlay>
  );
};